import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import Navbar from './components/Navbar';
import Home from './pages/Home';
import OnlinePooja from './pages/OnlinePooja';
import LiveDarshan from './pages/LiveDarshan';
import BhaktiStore from './pages/BhaktiStore';
import BhaktiYoga from './pages/BhaktiYoga';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import TestIntegration from './pages/TestIntegration';
import Footer from './components/Footer';
import './App.css';
import ProtectedRoute from './components/ProtectedRoute';
import DashboardPage from './pages/Dashboard/DashboardPage';
import ProfilePage from './pages/Dashboard/ProfilePage';
import MyBookings from './pages/Dashboard/MyBookings';
import AdminRoute from './components/AdminRoute';
import AdminDashboardPage from './pages/Admin/AdminDashboardPage';
import ManageBookings from './pages/Admin/ManageBookings';
import ManageProducts from './pages/Admin/ManageProducts';
import { CartProvider } from './contexts/CartContext';
import CheckoutPage from './pages/CheckoutPage';
import ShoppingCart from './components/ShoppingCart';
import MyOrders from './pages/Dashboard/MyOrders';
import ManageOrders from './pages/Admin/ManageOrders';
import { Toaster } from 'react-hot-toast';
import ManageCourses from './pages/Admin/ManageCourses';
import CourseEditor from './pages/Admin/CourseEditor';
import ManageEnrollments from './pages/Admin/ManageEnrollments';
import CourseDetailPage from './pages/CourseDetailPage';
import CoursePlayerPage from './pages/CoursePlayerPage';
import MyCourses from './pages/Dashboard/MyCourses';
import SubscriptionPage from './pages/SubscriptionPage';
import ManageCatalogues from './pages/Admin/ManageCatalogues';
import ManageDailySpiritualInfo from './pages/Admin/ManageDailySpiritualInfo';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import BhaktiJaapaPage from './pages/BhaktiJaapaPage';
import BhaktiSamadhan from './pages/BhaktiSamadhan';
import TermsPage from './pages/TermsPage';
import JaapaHistory from './pages/Dashboard/JaapaHistory';
import JaapaAnalyticsPage from './pages/Dashboard/JaapaAnalyticsPage';
import AdminOverview from './pages/Admin/AdminOverview';
import BhaktiBalChitraKatha from './pages/BhaktiBalChitraKatha';
import ManageBhaktiBalChitraKatha from './pages/Admin/ManageBhaktiBalChitraKatha';
import ManagePoojaServices from './pages/Admin/ManagePoojaServices';
import ManageBhaktiDarshan from './pages/Admin/ManageBhaktiDarshan';
import ContactPage from './pages/ContactPage';
import ProgressiveSignupPrompt from './components/ProgressiveSignupPrompt';
import AuthIdleLogoutGate from './components/AuthIdleLogoutGate';
import ManageVastuConsultations from './pages/Admin/ManageVastuConsultations';
import ManageVastuConsultants from './pages/Admin/ManageVastuConsultants';

// --- VAASTU MERGED IMPORTS ---
import VastuLaunchPage from './pages/VastuLaunchPage';
import MyConsultationsPage from './pages/MyConsultationsPage';
import ConsultantWorkPage from './pages/ConsultantWorkPage';
import ConsultantDirectoryPage from './pages/ConsultantDirectoryPage';
import ConsultantProfilePage from './pages/ConsultantProfilePage';
import VastuBookingPage from './pages/VastuBookingPage'; 

const ScrollToTopOnRouteChange = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [pathname]);

  return null;
};

// --- OPTIONAL: SCROLL DOWN FLOATING BUTTON ---
const ScrollDownAction = () => {
  const location = useLocation();
  const [canScrollDown, setCanScrollDown] = useState(false);

  useEffect(() => {
    const updateScrollAvailability = () => {
      const pageHeight = document.documentElement.scrollHeight;
      setCanScrollDown(window.scrollY + window.innerHeight < pageHeight - 8);
    };

    updateScrollAvailability();
    window.addEventListener('scroll', updateScrollAvailability, { passive: true });
    window.addEventListener('resize', updateScrollAvailability);

    return () => {
      window.removeEventListener('scroll', updateScrollAvailability);
      window.removeEventListener('resize', updateScrollAvailability);
    };
  }, [location.pathname]);

  if (!canScrollDown) return null;

  const scrollDown = () => {
    window.scrollBy({ top: Math.max(window.innerHeight * 0.78, 420), behavior: 'smooth' });
  };

  return (
    <button type="button" className="scroll-down-action" onClick={scrollDown} aria-label="Scroll down" title="Scroll down">
      <span aria-hidden="true">↓</span>
    </button>
  );
};

function App() {

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <LanguageProvider>
      <AuthProvider>
        <CartProvider>
          <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <ScrollToTopOnRouteChange />
          <div className="App">
            <Toaster
              position="top-center"
              reverseOrder={false}
              toastOptions={{
                duration: 4000,
                style: {
                  background: '#333',
                  color: '#fff',
                  boxShadow: '0 5px 15px rgba(0,0,0,0.2)',
                  fontFamily: 'var(--body-font)',
                },
              }}
            />
            <Navbar />
            <ShoppingCart />
            <AuthIdleLogoutGate />
            <ProgressiveSignupPrompt />
            <Routes>
              {/* Public Routes  */}
              <Route path="/" element={<Home />} />
              <Route path="/live-darshan" element={<LiveDarshan />} />
              <Route path="/bhakti-store" element={<BhaktiStore />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
              <Route path="/test-integration" element={<TestIntegration />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/privacy" element={<TermsPage />} />
              <Route path="/bhakti-samadhan" element={<BhaktiSamadhan />} />
              <Route path="/bhakti-bal-chitra-katha" element={<BhaktiBalChitraKatha />} />
              
              {/* Main Vastu Public Routes */}
              <Route path="/vastu" element={<VastuLaunchPage />} />
              <Route path="/vastu/consultants" element={<ConsultantDirectoryPage />} />
              <Route path="/vastu/consultants/:id" element={<ConsultantProfilePage />} />
              <Route path="/vastu/book" element={<ProtectedRoute><VastuBookingPage /></ProtectedRoute>} />

              {/* Dedicated workspace for Vastu Consultants (NOT an Admin Route) */}
              <Route
                path="/vastu-consultant-desk"
                element={
                  <ProtectedRoute>
                    <ConsultantWorkPage />
                  </ProtectedRoute>
                }
              />

              {/* Protected Routes */}
              <Route path="/profile" element={<ProtectedRoute></ProtectedRoute>} />
              <Route path="/bookings" element={<ProtectedRoute></ProtectedRoute>} />
              <Route path='/bhakti-yoga' element={<ProtectedRoute><BhaktiYoga /></ProtectedRoute>} />
              <Route path="/bhakti-yoga/courses/:id" element={<ProtectedRoute><CourseDetailPage /></ProtectedRoute>} />
              <Route path="/bhakti-jaapa" element={<ProtectedRoute><BhaktiJaapaPage /></ProtectedRoute>} />
              <Route path="/courses/player/:id" element={<ProtectedRoute><CoursePlayerPage /></ProtectedRoute>} />
              <Route path='/online-pooja' element={<ProtectedRoute><OnlinePooja /></ProtectedRoute>} />

              {/* User Dashboards */}
              <Route path='/dashboard' element={<ProtectedRoute><DashboardPage /></ProtectedRoute>}>
                <Route path="bookings" element={<MyBookings />} />
                <Route path="orders" element={<MyOrders />} />
                <Route path="courses" element={<MyCourses />} />
                <Route path="jaapa-analytics" element={<JaapaAnalyticsPage />} />
                <Route path="vastu-consultations" element={<MyConsultationsPage />} />
                <Route path="profile" element={<ProfilePage />} />
              </Route>

              <Route path='/checkout' element={<ProtectedRoute><CheckoutPage /></ProtectedRoute>} />
              <Route path="/subscribe" element={<ProtectedRoute><SubscriptionPage /></ProtectedRoute>} />

              {/* Admin Panel Routes (STRICTLY for Admins) */}
              <Route path="/admin" element={<AdminRoute><AdminDashboardPage /></AdminRoute>}>
                <Route path="dashboard" element={<AdminOverview />} />
                <Route path="bookings" element={<ManageBookings />} />
                <Route path="temple-catalogue" element={<ManagePoojaServices />} />
                <Route path="pooja-services" element={<ManagePoojaServices />} />
                <Route path='products' element={<ManageProducts />} />
                <Route path="orders" element={<ManageOrders />} />
                <Route path="catalogues" element={<ManageCatalogues />} />
                <Route path="daily-spiritual-info" element={<ManageDailySpiritualInfo />} />
                <Route path="bhakti-darshan" element={<ManageBhaktiDarshan />} />
                <Route path="bhakti-bal-chitra-katha" element={<ManageBhaktiBalChitraKatha />} />
                <Route path="vastu-consultations" element={<ManageVastuConsultations />} />
                <Route path="vastu-consultants" element={<ManageVastuConsultants />} />
                <Route path="courses" element={<ManageCourses />} />
                <Route path="courses/:courseId/edit" element={<CourseEditor />} />
                <Route path="enrollments" element={<ManageEnrollments />} />
              </Route>
            </Routes>
            <Footer />
            
            <ScrollDownAction />
            
          </div>
          </Router>
        </CartProvider>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;